import React, { useRef, useState } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import Draggable, { DraggableSlider } from '../wrappers/draggable';
import { navigate } from 'gatsby';

type CardDetails = {
  author_name?: string;
  author_title?: string;
  cta_text?: string;
  cta_url?: string;
  date?: string;
  image: string;
  name: string;
  stars: string;
  text: string;
  title: string;
};

const CardGallery: React.FC<{ block: any }> = ({ block }) => {
  const { reviews } = block;
  const cols = block.cols && block.cols > 0 ? Number.parseInt(block.cols) : 2;
  const [first, setFirst] = useState(0);
  const [visibleItems, setVisibleItems] = useState([
    ...reviews.slice(first, cols),
  ]);
  const animateRef = useRef(null);
  const themeClass = block.theme ? `card-gallery--${block.theme}` : '';
  const centeredClass = block.theme !== 'skinned' ? 'slider--centered' : '';
  const willSlide = reviews.length > cols;
  const slideClass = willSlide ? 'card-cells--slide' : '';

  const slideLeft = () => {
    const newFirst = (first + 1) % reviews.length;
    const added = (newFirst + cols - 1) % reviews.length;
    const newItems = [...visibleItems];
    newItems.push(reviews[added]);
    newItems.shift();
    setVisibleItems(newItems);
    setFirst(newFirst);
  };
  const slideRight = () => {
    const added = first - 1 < 0 ? reviews.length - 1 : first - 1;
    const newItems = [...visibleItems];
    newItems.pop();
    newItems.unshift(reviews[added]);
    setVisibleItems(newItems);
    setFirst(added);
  };
  const slideToggle = (event: React.MouseEvent<HTMLDivElement>) => {
    const dataIndex = event.currentTarget.getAttribute('data-index') || '0';
    const index = parseInt(dataIndex, 10);
    if (index !== first) {
      const last = index + cols;
      if (last > reviews.length) {
        setVisibleItems([reviews[reviews.length - 1], reviews[0]]);
      } else {
        setVisibleItems([
          ...reviews.slice(index, last, last > reviews.length ? -1 : 0),
        ]);
      }
      setFirst(index);
    }
  };

  const [visibleItemsRef] = useAutoAnimate<HTMLDivElement>();

  return (
    <section
      className={`block__card_gallery block ${block.css_class_name} ${themeClass}`}
    >
      <div className='container'>
        <Draggable
          triggerOnLeftDrag={slideLeft}
          triggerOnRightDrag={slideRight}
          ignoreDrag={!willSlide}
        >
          <div
            ref={visibleItemsRef}
            className={`row card-cells ${slideClass}`}
          >
            {visibleItems.map((item, index) => (
              <CardCell
                key={`cardcell-${(first + index) % reviews.length}`}
                details={item}
                cols={cols}
                theme={block.theme}
              />
            ))}
          </div>
          {willSlide && block.show_slider && (
            <div className={`slider--mt-sm ${centeredClass}`}>
              <DraggableSlider
                itemsCount={reviews.length}
                triggerOnClick={slideToggle}
                activeIndex={first}
                state={block.slider_theme}
              />
            </div>
          )}
        </Draggable>
        {willSlide && block.show_buttons && (
          <CardButtons
            slideLeft={slideLeft}
            slideRight={slideRight}
          />
        )}
      </div>
    </section>
  );
};

const CardButtons: React.FC<{
  slideLeft: () => void;
  slideRight: () => void;
  state?: string;
}> = ({ slideLeft, slideRight, state }) => {
  const stateClass = state ? `card-buttons--${state}` : '';
  return (
    <div className={`card-buttons ${stateClass}`}>
      <button
        className='card-button card-button--left'
        type='button'
        onClick={slideRight}
      >
        <div className='card-button__icon' />
      </button>
      <button
        className='card-button card-button--right'
        type='button'
        onClick={slideLeft}
      >
        <div className='card-button__icon' />
      </button>
    </div>
  );
};

const CardCell: React.FC<{
  details: CardDetails;
  cols: number;
  theme: string;
}> = ({ details, cols, theme }) => {
  return (
    <div className={`col-12 col-md-${12 / cols} gy-5`}>
      {(() => {
        switch (theme) {
          case 'white':
          case 'landing':
            return <Card details={details} />;
          case 'links':
            return <CardLinks details={details} />;
          case 'skinned':
          default:
            return <CardSkinned details={details} />;
        }
      })()}
    </div>
  );
};

const CardLinks: React.FC<{ details: CardDetails }> = ({ details }) => {
  const MOUSE_WHEEL = 1;
  const mouseDownHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    if (typeof window !== 'undefined' && event.button === MOUSE_WHEEL) {
      window.open(details.cta_url, '_blank', 'noopener');
    }
  };

  const handleClick = () => {
    if (details.cta_url) {
      navigate(details.cta_url);
    }
  };

  return (
    <div
      className='my-card'
      onClick={handleClick}
      onMouseDown={mouseDownHandler}
    >
      <div className='my-author'>
        <div className='my-author__image'>
          <img
            src={details.image}
            alt={`${details.name}'s review graphic`}
          />
        </div>
      </div>
      {details.title && <h4 className='my-card__title'>{details.title}</h4>}
      <div
        className='my-card__description'
        dangerouslySetInnerHTML={{ __html: details.text }}
      />
      <div className='my-card__link'>
        {details.cta_text}
        <span className='with-arrow'></span>
      </div>
    </div>
  );
};

const Card: React.FC<{ details: CardDetails }> = ({ details }) => {
  return (
    <div className='my-card'>
      <div className='my-author'>
        <div className='my-author__image'>
          <img
            src={details.image}
            alt={`${details.name}'s review graphic`}
          />
        </div>
      </div>
      {details.title && <h4 className='my-card__title'>{details.title}</h4>}
      <div
        className='my-card__description'
        dangerouslySetInnerHTML={{ __html: details.text }}
      />
      <div className='my-author'>
        <div className='my-author__details'>
          <span className='my-author__name'>{details.author_name}</span>
          <span className='my-author__title'>{details.author_title}</span>
        </div>
      </div>
    </div>
  );
};

const CardSkinned: React.FC<{ details: CardDetails }> = ({ details }) => {
  return (
    <div className='my-card my-card--skinned'>
      <div
        className='my-card__description'
        dangerouslySetInnerHTML={{ __html: details.text }}
      />
      <div className='my-author'>
        <div className='my-author__image'>
          <img
            src={details.image}
            alt={`${details.author_name}'s review graphic`}
          />
        </div>
      </div>
    </div>
  );
};

export default CardGallery;
export { CardButtons };
